import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import {
  UitkDateRangeGrid,
  UitkDateRangeGridResult,
  UitkDateRangeGridCellResult,
  UitkDateRangeGridHeaderLegend,
  UitkDateRangeGridHeader
} from 'uitk-react-date-range-grid';
import { Level, liveAnnounce } from 'uitk-react-utils';

import { FlexibleSearchGridFooter } from './';
import {
  analyticsConfigMapper,
  FlexSearchPerformance,
  getValueText,
  trackAnalytics,
  getFlexibleSearchGridResultTheme,
  isFlexibleSearchCellSelected,
  getSelectedCellData,
  getFooterProps
} from 'utility';
import { useStores } from 'utility/hooks';
import {
  FlightsFlexibleSearch,
  FlightsFlexSearchResults,
  FlightsFlexSearchHeaderCell,
  FlightsFlexSearchLegendCell
} from '__generated__/api/types';

interface BaseFlexibleSearchGridProps {
  displayFooter?: boolean;
}

export const BaseFlexibleSearchGrid = observer(({ displayFooter }: BaseFlexibleSearchGridProps) => {
  const {
    analytics,
    context,
    flexibleSearchResultsStore,
    routeStore,
    searchCriteriaStore,
    searchFormStore,
    cheapestListingInfoStore
  } = useStores();

  const {
    searchTriggerAction,
    resultsGrid,
    departureLegend,
    titleAccessibility,
    coverageAnalytics
  } = flexibleSearchResultsStore;
  let returnLegend;
  let searchedCell;

  if (!resultsGrid) {
    return null;
  }

  useEffect(() => {
    performance.mark(FlexSearchPerformance.RENDER_FLEX_SEARCH_END_MARK);
    performance.measure(
      FlexSearchPerformance.RENDER_FLEX_SEARCH_DURATION,
      FlexSearchPerformance.RENDER_FLEX_SEARCH_START_MARK,
      FlexSearchPerformance.RENDER_FLEX_SEARCH_END_MARK
    );
    trackAnalytics(coverageAnalytics, analytics);
  });

  const results = resultsGrid.results as FlightsFlexibleSearch.Results[][];

  const transformedResults: UitkDateRangeGridResult[][] = results.map((resultRow: FlightsFlexibleSearch.Results[]) => {
    return resultRow.map((result: FlightsFlexibleSearch.Results) => {
      if ('nextSearchCriteria' in (result as FlightsFlexSearchResults.Fragment)) {
        const {
          nextSearchCriteria,
          selectAnalytics,
          theme,
          valueText,
          accessibility
        } = result as FlightsFlexSearchResults.Fragment;
        const analyticsConfig = selectAnalytics && analyticsConfigMapper(selectAnalytics, analytics);

        if (isFlexibleSearchCellSelected(theme)) {
          searchedCell = result;
        }

        /* covered by automation */
        /* istanbul ignore next */
        /* eslint-disable @typescript-eslint/no-use-before-define */
        const callback = useCallback(() => {
          setSelectedCell(getSelectedCellData(result));
          setFooterProps(getFooterProps(result, searchTriggerAction, triggerSearch, analyticsConfig));

          searchCriteriaStore.syncWithNextSearchCriteria(nextSearchCriteria);
          searchCriteriaStore.clearSortFilterSearchCriteria();
          searchCriteriaStore.clearJourneysContinuationId();
          searchFormStore.syncJourneysWithSearchCriteriaStore(searchCriteriaStore);

          if (!displayFooter) {
            triggerSearch();
          }
        }, [nextSearchCriteria, result, analyticsConfig]);

        return {
          analytics: selectAnalytics && analyticsConfigMapper(selectAnalytics, analytics),
          onClickCallback: callback,
          priceText: getValueText(cheapestListingInfoStore, valueText, theme, analytics),
          theme: getFlexibleSearchGridResultTheme(theme),
          accessibilityText: accessibility && accessibility.highlightDescription
        } as UitkDateRangeGridCellResult;
      } else if ('icon' in (result as FlightsFlexSearchLegendCell.Fragment)) {
        const { icon, valueText } = result as FlightsFlexSearchLegendCell.Fragment;
        returnLegend = {
          icon: icon,
          labelText: valueText
        } as UitkDateRangeGridHeaderLegend;
      } else {
        const { valueText, labelText, headerTheme } = result as FlightsFlexSearchHeaderCell.Fragment;

        return {
          dateText: valueText,
          dayText: labelText,
          headerTheme: getFlexibleSearchGridResultTheme(headerTheme)
        } as UitkDateRangeGridHeader;
      }
    });
  });

  const { accessibility, selectAnalytics, nextSearchCriteria, valueText, journeyDate, summary } = searchedCell;

  const [selectedCell, setSelectedCell] = useState({
    accessibility: accessibility,
    nextSearchCriteria: nextSearchCriteria
  });

  const triggerSearch = () => {
    const { accessibility } = selectedCell;

    searchCriteriaStore.triggerSearch(routeStore, context, true);
    liveAnnounce(accessibility.selectionDescription, Level.POLITE);
  };

  const [footerProps, setFooterProps] = useState({
    analytics: analyticsConfigMapper(selectAnalytics, analytics),
    buttonText: searchTriggerAction,
    dateText: journeyDate,
    onClick: triggerSearch,
    priceText: valueText,
    subText: summary
  });

  return (
    <>
      <UitkDateRangeGrid
        a11yTitle={titleAccessibility}
        columnLegend={departureLegend}
        rowLegend={returnLegend}
        results={transformedResults}
      />
      {displayFooter && <FlexibleSearchGridFooter {...footerProps} />}
    </>
  );
});
